/** Stock Upload page permission code. */
export const STOCK_UPLOAD_PERMISSION_CODE = 300;

/** Restricted access: only Inventory + Stock Upload are allowed. */
export const INVENTORY_STOCK_ONLY_PERMISSION_CODE = 400;

/** User ID for which permission 400 applies the two-page-only restriction. */
export const INVENTORY_STOCK_ONLY_USER_ID = 1738666630;

/**
 * Normalize permission codes from login payload (array, CSV string, or number).
 * @param {unknown} raw
 * @returns {number[]}
 */
export function parsePermissionCodes(raw) {
  if (raw == null || raw === "") return [];
  if (typeof raw === "number" && Number.isFinite(raw)) return [raw];
  if (Array.isArray(raw)) {
    return raw.map(Number).filter((n) => Number.isFinite(n));
  }
  if (typeof raw === "string") {
    return raw
      .split(",")
      .map((p) => Number(p.trim()))
      .filter((n) => Number.isFinite(n));
  }
  return [];
}

/**
 * Collect permission codes from user.userDataObj only.
 * @param {object | null | undefined} user
 * @returns {number[]}
 */
export function getUserPermissionCodes(user) {
  if (!user?.userDataObj) return [];
  const set = new Set();
  for (const n of parsePermissionCodes(user.userDataObj.permissioncodes)) set.add(n);
  for (const n of parsePermissionCodes(user.userDataObj.permission_codes)) set.add(n);
  return [...set];
}

/**
 * @param {object | null | undefined} user
 * @param {number|string} code
 */
export function hasPermissionCode(user, code) {
  const n = Number(code);
  if (!Number.isFinite(n)) return false;
  return getUserPermissionCodes(user).includes(n);
}

/**
 * @param {object | null | undefined} user
 * @param {Array<number|string>} codes
 */
export function hasAnyPermissionCode(user, codes = []) {
  return codes.some((code) => hasPermissionCode(user, code));
}

/**
 * Restricted Inventory + Stock Upload only when:
 * - permission code 400 is present, AND
 * - user_id matches INVENTORY_STOCK_ONLY_USER_ID
 */
export function isInventoryStockOnlyUser(user) {
  const userId = user?.userDataObj?.user_id;
  if (userId == null) return false;
  return (
    hasPermissionCode(user, INVENTORY_STOCK_ONLY_PERMISSION_CODE) &&
    String(userId) === String(INVENTORY_STOCK_ONLY_USER_ID)
  );
}

/** Stock Upload: allowed with 300, or for the restricted 400 + specific user. */
export function canAccessStockUpload(user) {
  return (
    hasPermissionCode(user, STOCK_UPLOAD_PERMISSION_CODE) ||
    isInventoryStockOnlyUser(user)
  );
}

/** Paths allowed for permission-400 users. */
export const INVENTORY_STOCK_ONLY_PATHS = ["/", "/stock-upload"];

export function canAccessPath(user, path) {
  if (isInventoryStockOnlyUser(user)) {
    return INVENTORY_STOCK_ONLY_PATHS.includes(path);
  }
  if (path === "/stock-upload") {
    return canAccessStockUpload(user);
  }
  return true;
}
